/* CloudSocial editor bundle entry (Phase 5e).
 *
 * Exposes `window.CSEditor` with two mount functions:
 *   - mountComposer({ container, initialDoc, onChange, ... })
 *       Article composer with title-as-first-node + slash + bubble menus.
 *   - mountBodyEditor({ textarea, variant, onChange })
 *       Body-only Tiptap that replaces a <textarea data-cs-editor="...">.
 *       Variants: 'full' | 'comment' | 'chat'. Mirrors HTML to the textarea
 *       on every change so existing form posts keep working.
 *
 * Both share a single Tiptap install. The bundle also auto-attaches to any
 * `<textarea data-cs-editor>` on DOMContentLoaded — mirrors the old
 * cs-editor.js contract so comment / forum / directory templates keep
 * working without per-page changes.
 */
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import BubbleMenu from '@tiptap/extension-bubble-menu';

import { TitleBody } from './title-body.js';
import { SlashCommand } from './slash-command.js';
import { configureBubbleMenu } from './bubble-menu-config.js';

// Tippy.js base CSS is shipped inline in app/static/css/editor.css —
// importing the package CSS here gets tree-shaken by esbuild because the
// package marks it as side-effect-free. Don't re-add the import.

// ── Helpers ────────────────────────────────────────────────────────────

function csrf() {
  return document.querySelector('meta[name="csrf-token"]')?.content
      || document.querySelector('input[name="csrf_token"]')?.value || '';
}

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await fetch('/api/uploads/r2-image', {
    method: 'POST', body: fd,
    headers: { 'X-CSRFToken': csrf() },
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || 'Upload failed');
  }
  const { url } = await r.json();
  return url;
}

function attachPasteAndDrop(editor, container) {
  // Paste / drop image upload — replace the file with a remote <img> after upload.
  function handleFiles(files) {
    const file = Array.from(files).find(f => /^image\//.test(f.type));
    if (!file) return false;
    uploadImage(file).then(url => {
      editor.chain().focus().setImage({ src: url }).run();
    }).catch(e => {
      // eslint-disable-next-line no-alert
      alert(e.message || 'Image upload failed');
    });
    return true;
  }
  container.addEventListener('paste', (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === 'file' && /^image\//.test(it.type)) {
        e.preventDefault();
        const f = it.getAsFile();
        if (f) handleFiles([f]);
        return;
      }
    }
  });
  container.addEventListener('drop', (e) => {
    if (e.dataTransfer && e.dataTransfer.files.length) {
      const handled = handleFiles(e.dataTransfer.files);
      if (handled) e.preventDefault();
    }
  });
}

// ── Article composer (title + body) ───────────────────────────────────

export function mountComposer({ container, bubbleMenuEl, initialDoc, onChange, onSubmit }) {
  const editor = new Editor({
    element: container,
    extensions: [
      StarterKit.configure({
        document: false,        // overridden by TitleBody below
        heading: { levels: [2, 3] },
      }),
      ...TitleBody,
      Placeholder.configure({
        placeholder: ({ node }) =>
          node.type.name === 'title' ? 'Title (optional)' : 'Write something',
        showOnlyCurrent: false,
      }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      Underline,
      ...(bubbleMenuEl ? [BubbleMenu.configure(configureBubbleMenu(bubbleMenuEl))] : []),
      SlashCommand,
    ],
    content: initialDoc || {
      type: 'doc',
      content: [{ type: 'title' }, { type: 'paragraph' }],
    },
    onUpdate: ({ editor }) => onChange?.({
      json: editor.getJSON(),
      html: editor.getHTML(),
      title: extractTitleText(editor),
      bodyHtml: extractBodyHtml(editor),
    }),
  });

  attachPasteAndDrop(editor, container);

  // Ctrl/Cmd+Enter publishes
  container.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.();
    }
  });

  return editor;
}

function extractTitleText(editor) {
  const json = editor.getJSON();
  const titleNode = (json.content || []).find(n => n.type === 'title');
  if (!titleNode) return '';
  const txt = (titleNode.content || []).map(c => c.text || '').join('');
  return txt.trim();
}

function extractBodyHtml(editor) {
  // Tiptap's getHTML gives the full doc; strip the leading <h1.cs-title>.
  const html = editor.getHTML();
  return html.replace(/^\s*<h1\s+class="cs-title"[^>]*>[\s\S]*?<\/h1>/i, '').trim();
}

// ── Body-only editor (replaces Quill on comments / forum / directory) ─

export function mountBodyEditor({ textarea, variant, onChange }) {
  if (!textarea || textarea.dataset.csEditorAttached === '1') return null;
  textarea.dataset.csEditorAttached = '1';

  const v = variant || textarea.dataset.csEditor || 'full';
  const placeholder = textarea.placeholder || (v === 'comment' ? 'Write a comment…' : 'Write something…');

  // Build the wrapper.
  const wrap = document.createElement('div');
  wrap.className = 'cs-editor-wrap cs-editor-' + v;
  const toolbar = document.createElement('div');
  toolbar.className = 'cs-editor-toolbar';
  const bodyEl = document.createElement('div');
  bodyEl.className = 'cs-editor-body';
  wrap.appendChild(toolbar);
  wrap.appendChild(bodyEl);

  textarea.parentNode.insertBefore(wrap, textarea);
  textarea.style.display = 'none';

  const initialHtml = (textarea.value || '').trim();

  const exts = [
    StarterKit.configure({
      heading: v === 'comment' ? false : { levels: [2, 3] },
      codeBlock: v === 'comment' ? false : {},
      horizontalRule: v === 'comment' ? false : {},
    }),
    Placeholder.configure({ placeholder, showOnlyCurrent: false }),
    Link.configure({ openOnClick: false, autolink: true }),
    Image,
    Underline,
  ];

  const editor = new Editor({
    element: bodyEl,
    extensions: exts,
    content: initialHtml || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      textarea.value = html;
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      onChange?.(html);
    },
  });

  // Build the toolbar buttons per variant.
  buildToolbar(toolbar, editor, v);

  // Image paste/drop
  attachPasteAndDrop(editor, bodyEl);

  // Stash for outside callers (the legacy composer-modal.js peeks at this).
  textarea.__cs_editor = editor;

  return editor;
}

function buildToolbar(toolbar, editor, variant) {
  toolbar.innerHTML = '';
  const make = (label, title, fn, isActive) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'cs-tb-btn';
    b.title = title;
    b.innerHTML = label;
    b.addEventListener('mousedown', (e) => { e.preventDefault(); fn(); });
    if (isActive) {
      editor.on('selectionUpdate', () => b.classList.toggle('cs-tb-active', isActive()));
      editor.on('transaction', () => b.classList.toggle('cs-tb-active', isActive()));
    }
    toolbar.appendChild(b);
    return b;
  };
  const sep = () => { const s = document.createElement('span'); s.className = 'cs-tb-sep'; toolbar.appendChild(s); };

  make('<b>B</b>', 'Bold (Ctrl+B)',
    () => editor.chain().focus().toggleBold().run(),
    () => editor.isActive('bold'));
  make('<i>I</i>', 'Italic (Ctrl+I)',
    () => editor.chain().focus().toggleItalic().run(),
    () => editor.isActive('italic'));
  make('<u>U</u>', 'Underline',
    () => editor.chain().focus().toggleUnderline().run(),
    () => editor.isActive('underline'));
  if (variant !== 'comment') {
    make('<s>S</s>', 'Strike',
      () => editor.chain().focus().toggleStrike().run(),
      () => editor.isActive('strike'));
    sep();
    make('H<sub>2</sub>', 'Heading 2',
      () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      () => editor.isActive('heading', { level: 2 }));
    make('H<sub>3</sub>', 'Heading 3',
      () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      () => editor.isActive('heading', { level: 3 }));
    sep();
    make('“”', 'Blockquote',
      () => editor.chain().focus().toggleBlockquote().run(),
      () => editor.isActive('blockquote'));
    make('&lt;/&gt;', 'Code block',
      () => editor.chain().focus().toggleCodeBlock().run(),
      () => editor.isActive('codeBlock'));
  }
  sep();
  make('•', 'Bulleted list',
    () => editor.chain().focus().toggleBulletList().run(),
    () => editor.isActive('bulletList'));
  make('1.', 'Numbered list',
    () => editor.chain().focus().toggleOrderedList().run(),
    () => editor.isActive('orderedList'));
  sep();
  make('🔗', 'Link (Ctrl+K)', () => {
    const prev = editor.getAttributes('link').href || '';
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL:', prev || 'https://');
    if (url === null) return;
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  });
  make('🖼', 'Insert image', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = async () => {
      const f = input.files && input.files[0];
      if (!f) return;
      try {
        const url = await uploadImage(f);
        editor.chain().focus().setImage({ src: url }).run();
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert(e.message || 'Upload failed');
      }
    };
    input.click();
  });
}

// ── Auto-attach to any <textarea data-cs-editor> on DOM ready ─────────

export function autoAttach() {
  document.querySelectorAll('textarea[data-cs-editor]').forEach(t => {
    try { mountBodyEditor({ textarea: t, variant: t.dataset.csEditor }); }
    catch (e) { console.error('mountBodyEditor failed', e); t.style.display = ''; }
  });
}

// ── Public API ────────────────────────────────────────────────────────
//
// esbuild is configured with format: 'iife' + globalName: 'CSEditor', so
// this file's top-level exports (mountComposer, mountBodyEditor,
// uploadImage, autoAttach) become properties of the global window.CSEditor
// object automatically. Don't manually assign window.CSEditor here — the
// outer `var CSEditor = (...)()` wrapper esbuild emits would clobber it
// with the module-exports namespace AFTER our assignment runs, dropping
// any properties that aren't top-level exports.

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoAttach);
  } else {
    autoAttach();
  }
}
